// src/components/ReadBookScreen.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getBackblazeDownloadUrl } from '../config/backblaze';

import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { highlightPlugin, Trigger } from '@react-pdf-viewer/highlight';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/highlight/lib/styles/index.css';

// ✅ استيراد pdf-lib للـ Watermark (npm install pdf-lib)
import { PDFDocument, rgb, degrees, StandardFonts } from 'pdf-lib';

import { supabase } from '../services/supabaseService';
import offlineStorage from '../services/offlineStorage';
import koalaImg from '../assets/koala.jpg';

// ─────────────────────────────────────────────
//  CSS
// ─────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    background: #080A0E; color: #fff;
    font-family: 'Tajawal', sans-serif;
    direction: rtl; height: 100vh; overflow: hidden;
  }
  .rpv-core__viewer,
  .rpv-default-layout__body { background: #15181E !important; }
  ::selection { background: rgba(124,111,255,0.25) !important; color: inherit !important; }

  /* ── Header ── */
  .custom-header {
    height: 56px; background: #0D0F16;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 16px; z-index: 9999; position: relative; gap: 12px;
  }
  .header-title {
    font-weight: 800; font-size: 14px; color: #e0e0e0;
    display: flex; align-items: center; gap: 8px; white-space: nowrap;
  }
  .header-section { display: flex; align-items: center; gap: 8px; }
  .section-label  { font-size: 11px; color: #555; white-space: nowrap; }

  /* ── Undo/Redo ── */
  .undo-redo-group {
    display: flex; align-items: center; gap: 4px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px; padding: 4px 6px;
  }
  .undo-redo-btn {
    width: 32px; height: 32px; border-radius: 7px; border: none;
    background: transparent; color: #aaa; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; transition: all 0.18s; position: relative;
  }
  .undo-redo-btn:hover:not(:disabled) {
    background: rgba(124,111,255,0.15); color: #fff; transform: scale(1.05);
  }
  .undo-redo-btn:active:not(:disabled) { transform: scale(0.95); }
  .undo-redo-btn:disabled { opacity: 0.25; cursor: not-allowed; }
  .undo-redo-btn .badge {
    position: absolute; top: -4px; left: -4px;
    background: #7C6FFF; color: #fff; font-size: 9px; font-weight: 700;
    width: 16px; height: 16px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-family: monospace; pointer-events: none;
  }
  .divider { width: 1px; height: 20px; background: rgba(255,255,255,0.08); margin: 0 4px; }

  /* ── Color Picker ── */
  .color-picker {
    width: 22px; height: 22px; border-radius: 50%;
    cursor: pointer; border: 2px solid transparent; transition: all 0.2s;
  }
  .color-picker.active {
    border-color: #fff; transform: scale(1.15);
    box-shadow: 0 0 0 3px rgba(255,255,255,0.12);
  }
  .color-picker:not(.active):hover { transform: scale(1.1); border-color: rgba(255,255,255,0.4); }

  /* ── Highlight Popup ── */
  .highlight-popup {
    background: #0D0F16; border: 1px solid rgba(124,111,255,0.5);
    border-radius: 12px; padding: 8px 14px;
    display: flex; align-items: center; gap: 10px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.8); backdrop-filter: blur(10px);
  }
  .highlight-popup-label { font-size: 11px; color: #666; font-family: 'Tajawal',sans-serif; white-space: nowrap; }
  .popup-color-btn {
    width: 20px; height: 20px; border-radius: 50%;
    cursor: pointer; border: 2px solid transparent; transition: all 0.15s;
  }
  .popup-color-btn:hover { transform: scale(1.25); border-color: rgba(255,255,255,0.8); }
  .popup-copy-btn {
    height: 26px; padding: 0 10px; border-radius: 7px; border: none;
    background: rgba(124,111,255,0.2); color: #c0b8ff;
    font-size: 11px; font-family: 'Tajawal',sans-serif;
    cursor: pointer; transition: all 0.15s; white-space: nowrap;
  }
  .popup-copy-btn:hover { background: rgba(124,111,255,0.4); color: #fff; }

  /* ── Loading / Error screens ── */
  .loading-screen {
    display: flex; flex-direction: column;
    justify-content: center; align-items: center;
    height: 100vh; gap: 16px;
    background: #080A0E; color: #fff;
    font-family: 'Tajawal', sans-serif;
  }
  .loading-spinner {
    width: 40px; height: 40px; border-radius: 50%;
    border: 3px solid rgba(124,111,255,0.2);
    border-top-color: #7C6FFF;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-text { font-size: 15px; color: #888; }

  /* ── Watermark badge (اختياري - يظهر في الـ header) ── */
  .watermark-badge {
    display: flex; align-items: center; gap: 5px;
    background: rgba(255,200,0,0.08); border: 1px solid rgba(255,200,0,0.2);
    border-radius: 8px; padding: 4px 10px;
    font-size: 10px; color: rgba(255,200,0,0.6); white-space: nowrap;
  }

  /* ── زر التحميل ── */
  .download-btn {
    height: 32px; padding: 0 12px; border-radius: 8px; border: none;
    background: rgba(255,255,255,0.06); color: #aaa; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    gap: 6px; font-size: 12px; font-family: 'Tajawal', sans-serif;
    transition: all 0.18s; white-space: nowrap;
    border: 1px solid rgba(255,255,255,0.08);
  }
  .download-btn:hover { background: rgba(124,111,255,0.15); color: #fff; }
  .download-btn:active { transform: scale(0.96); }

  /* ── Splash Screen ── */
  .splash-screen {
    position: fixed; inset: 0; z-index: 99999;
    background: #080A0E;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 24px; font-family: 'Tajawal', sans-serif;
    animation: splashFadeOut 0.6s ease 3.3s forwards;
  }
  @keyframes splashFadeOut {
    from { opacity: 1; pointer-events: all; }
    to   { opacity: 0; pointer-events: none; }
  }
  .splash-logo-ring {
    width: 130px; height: 130px; border-radius: 36px;
    overflow: hidden;
    border: 2px solid rgba(255,255,255,0.12);
    box-shadow: 0 0 0 10px rgba(255,255,255,0.04), 0 20px 60px rgba(0,0,0,0.8);
    animation: splashPop 0.65s cubic-bezier(0.16,1,0.3,1) forwards;
    opacity: 0;
  }
  .splash-logo-ring img { width: 100%; height: 100%; object-fit: cover; display: block; }
  @keyframes splashPop {
    from { opacity:0; transform: scale(0.72); }
    to   { opacity:1; transform: scale(1);    }
  }
  .splash-name {
    font-size: 26px; font-weight: 800; color: #ffffff;
    letter-spacing: 1px; opacity: 0;
    animation: splashSlideUp 0.5s ease 0.45s forwards;
  }
  .splash-sub {
    font-size: 11px; color: rgba(200,200,200,0.5);
    letter-spacing: 4px; text-transform: uppercase;
    opacity: 0; animation: splashSlideUp 0.5s ease 0.65s forwards;
    margin-top: -10px;
  }
  .splash-bar-wrap {
    width: 140px; height: 3px; border-radius: 99px;
    background: rgba(255,255,255,0.1); overflow: hidden;
    opacity: 0; animation: splashSlideUp 0.5s ease 0.85s forwards;
  }
  .splash-bar {
    height: 100%; width: 0%; border-radius: 99px;
    background: linear-gradient(90deg, #ffffff 0%, #888 100%);
    animation: splashBarFill 2.55s cubic-bezier(0.4,0,0.2,1) 0.95s forwards;
  }
  @keyframes splashBarFill { from{width:0%} to{width:100%} }
  @keyframes splashSlideUp {
    from { opacity:0; transform: translateY(14px); }
    to   { opacity:1; transform: translateY(0);     }
  }

  /* ── Toast ── */
  .toast {
    position: fixed; bottom: 24px; left: 50%;
    transform: translateX(-50%);
    background: #1E2130; border: 1px solid rgba(124,111,255,0.4);
    border-radius: 10px; padding: 10px 20px;
    color: #ccc; font-size: 13px; font-family: 'Tajawal',sans-serif;
    z-index: 99999; pointer-events: none; white-space: nowrap;
    box-shadow: 0 4px 20px rgba(0,0,0,0.6);
    animation: toastIn 0.25s ease, toastOut 0.3s ease 1.4s forwards;
  }
  @keyframes toastIn  { from{opacity:0;transform:translateX(-50%) translateY(10px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
  @keyframes toastOut { from{opacity:1} to{opacity:0} }

  /* ── حماية الطباعة ── */
  @media print {
    body, body * { visibility:hidden !important; display:none !important; }
    body::after {
      content:'هذا المحتوى محمي ولا يمكن طباعته';
      visibility:visible !important; display:block !important;
      position:absolute; top:50%; left:50%;
      transform:translate(-50%,-50%);
      font-size:24px; color:#000; background:#fff; padding:24px; border-radius:8px;
    }
  }
`;

// ─────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────
const COLORS = [
  { hex: '#FFE066', label: 'أصفر'    },
  { hex: '#69FF94', label: 'أخضر'    },
  { hex: '#FF6B9D', label: 'وردي'    },
  { hex: '#5DADE2', label: 'أزرق'    },
  { hex: '#FF8C42', label: 'برتقالي' },
];

const MAX_HISTORY  = 50;
const PDF_WORKER_URL = '/pdf.worker.min.js';
const PDF_WORKER_CDN = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

// ─────────────────────────────────────────────
//  ✅ دالة إضافة الـ Watermark
// ─────────────────────────────────────────────
// ✅ بعد
async function addWatermarkToPdf(pdfBlob, userInfo) {
  try {
    const { PDFDocument, rgb, degrees, StandardFonts } = await import('pdf-lib');
    const arrayBuffer = await pdfBlob.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const pages = pdfDoc.getPages();
    const email   = userInfo?.email || '';
    const userId  = userInfo?.id    || '';
    const shortId = userId.slice(0, 8).toUpperCase();
    const mainText   = email || shortId;
    const footerText = `Protected - ${mainText}`;

    for (const page of pages) {
      const { width, height } = page.getSize();

      // ✅ إنشاء صفحة جديدة فوق المحتوى الحالي عبر form XObject
      // رسم مستطيل شبه شفاف كـ overlay أولاً
      page.drawRectangle({
        x: 0, y: 0,
        width, height,
        color: rgb(1, 1, 1),
        opacity: 0,         // شفاف تماماً - فقط لفرض layer جديد
      });

      // ── Watermark مائل كبير - وسط الصفحة ──
      const textWidth = mainText.length * 18;
      page.drawText(mainText, {
        x: width / 2 - textWidth / 2,
        y: height / 2,
        size: 40,
        font,
        color: rgb(0.8, 0.2, 0.2),   // ✅ أحمر ليظهر فوق الصور
        opacity: 0.25,
        rotate: degrees(45),
      });

      // ── نسخة ثانية معاكسة ──
      page.drawText(mainText, {
        x: width / 2 - textWidth / 2,
        y: height / 2 + 120,
        size: 28,
        font,
        color: rgb(0.8, 0.2, 0.2),
        opacity: 0.18,
        rotate: degrees(45),
      });

      // ── أسفل الصفحة ──
      page.drawText(footerText, {
        x: 20, y: 10,
        size: 8, font,
        color: rgb(0.6, 0.1, 0.1),
        opacity: 0.6,
      });

      // ── أعلى يسار ──
      page.drawText(mainText, {
        x: 20, y: height - 20,
        size: 9, font,
        color: rgb(0.6, 0.1, 0.1),
        opacity: 0.6,
      });

      // ── أعلى يمين ──
      page.drawText(shortId, {
        x: width - (shortId.length * 7) - 10,
        y: height - 20,
        size: 9, font,
        color: rgb(0.6, 0.1, 0.1),
        opacity: 0.6,
      });
    }

    const watermarkedBytes = await pdfDoc.save();
    return watermarkedBytes;

  } catch (err) {
    console.warn('[ReadBook] Watermark failed:', err);
    const arrayBuffer = await pdfBlob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }
}
// ─────────────────────────────────────────────
//  Helper Functions
// ─────────────────────────────────────────────
async function getAllCachedPDFKeys() {
  try {
    const db = await new Promise((resolve, reject) => {
      const req = indexedDB.open('khidma_offline_db');
      req.onsuccess = (e) => resolve(e.target.result);
      req.onerror   = () => reject();
    });
    return await new Promise((resolve) => {
      try {
        const tx  = db.transaction('pdfs', 'readonly');
        const req = tx.objectStore('pdfs').getAllKeys();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror   = () => resolve([]);
      } catch { resolve([]); }
    });
  } catch { return []; }
}

async function toBlobUrl(data) {
  if (!data) return null;
  if (typeof data === 'string' && data.startsWith('blob:'))  return data;
  if (typeof data === 'string' && data.startsWith('data:')) {
    const blob = await (await fetch(data)).blob();
    return URL.createObjectURL(blob);
  }
  if (data instanceof Blob) return URL.createObjectURL(data);
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data))
    return URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  return null;
}

// ✅ تحويل Blob أوفلاين لـ Uint8Array مباشرة (بدون blob URL)
async function toUint8Array(data) {
  if (!data) return null;
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer);
  if (data instanceof Blob) return new Uint8Array(await data.arrayBuffer());
  if (typeof data === 'string' && data.startsWith('data:')) {
    const blob = await (await fetch(data)).blob();
    return new Uint8Array(await blob.arrayBuffer());
  }
  return null;
}

async function resolveWorkerUrl() {
  try {
    if ('caches' in window) {
      const cached = await caches.match('/pdf.worker.min.js');
      if (cached) return PDF_WORKER_URL;
    }
    const res = await fetch(PDF_WORKER_URL, { method: 'HEAD', cache: 'force-cache' });
    if (res.ok) return PDF_WORKER_URL;
  } catch {}
  return PDF_WORKER_CDN;
}

// ─────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────
export default function ReadBookScreen() {
  const location = useLocation();
  const [params] = useSearchParams();

  const bookId      = params.get('book') || location.state?.serviceId;
  const directPdfUrl = location.state?.pdfUrl;

  const [state,        setState]       = useState('loading');
  const [showSplash,   setShowSplash]  = useState(true);
  const [pdfSrc,       setPdfSrc]      = useState(null);   // ✅ سيكون { data: Uint8Array }
  const [workerUrl,    setWorkerUrl]   = useState(null);
  const [currentColor, setCurrentColor] = useState(COLORS[0].hex);
  const [toastMsg,     setToastMsg]    = useState(null);
  const [history,      setHistory]     = useState([[]]);
  const [historyIdx,   setHistoryIdx]  = useState(0);
  const [userEmail,    setUserEmail]   = useState('');     // ✅ لعرض الـ watermark badge

  const notes   = history[historyIdx] ?? [];
  const canUndo = historyIdx > 0;
  const canRedo = historyIdx < history.length - 1;

  const currentColorRef  = useRef(currentColor);
  const loadedBookIdRef  = useRef(null);
  const toastTimer       = useRef(null);
  const currentUserIdRef = useRef(null);
  const saveDebounce     = useRef(null);
  const pdfBlobUrlRef    = useRef(null);

  useEffect(() => { currentColorRef.current = currentColor; }, [currentColor]);

  // ── Toast ──
  const showToast = useCallback((msg) => {
    clearTimeout(toastTimer.current);
    setToastMsg(null);
    setTimeout(() => setToastMsg(msg), 10);
    toastTimer.current = setTimeout(() => setToastMsg(null), 1800);
  }, []);

  // ── History ──
  const pushNotes = useCallback((newNotes) => {
    setHistory(prev => {
      const trimmed = prev.slice(0, historyIdx + 1);
      return [...trimmed, newNotes].slice(-MAX_HISTORY);
    });
    setHistoryIdx(prev => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [historyIdx]);

  const undo = useCallback(() => {
    if (!canUndo) return;
    setHistoryIdx(i => i - 1);
    showToast('↩️  تم التراجع');
  }, [canUndo, showToast]);

  const redo = useCallback(() => {
    if (!canRedo) return;
    setHistoryIdx(i => i + 1);
    showToast('↪️  تم الإعادة');
  }, [canRedo, showToast]);

  const handleDownload = useCallback(() => {
    const url = pdfBlobUrlRef.current;
    if (!url) { showToast('⚠️ الملف غير جاهز بعد'); return; }
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'book.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    showToast('⬇️ جاري التحميل...');
  }, [showToast]);

  // ─────────────────────────────────────────────
  //  ✅ useEffect الرئيسي

  // ─────────────────────────────────────────────
  //  ✅ useEffect الرئيسي لتحميل الكتاب
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!bookId && !directPdfUrl) { setState('error'); return; }
    if (loadedBookIdRef.current === bookId) return;
    loadedBookIdRef.current = bookId;

    async function init() {
      setState('loading');
      setPdfSrc(null);
      setWorkerUrl(null);

      const resolvedWorker = await resolveWorkerUrl();
      setWorkerUrl(resolvedWorker);

      // ── تحميل الـ session ──
      const cachedSession = await offlineStorage.loadSession();
      const userId = cachedSession?.userId || null;
      currentUserIdRef.current = userId;

      // ── ✅ جلب بيانات المستخدم للـ watermark ──
      let userInfo = { id: userId, email: '' };
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          userInfo = { id: user.id, email: user.email || '' };
          setUserEmail(user.email || user.id?.slice(0, 8) || '');
        }
      } catch {}

      // ── تحميل التظليلات المحفوظة ──
      if (userId) {
        try {
          const savedNotes = await offlineStorage.loadHighlights(userId, bookId);
          if (savedNotes?.length > 0) {
            setHistory([[...savedNotes]]);
            setHistoryIdx(0);
          } else {
            const lsNotes = localStorage.getItem(`pdf_notes_${bookId}`);
            if (lsNotes) {
              const parsed = JSON.parse(lsNotes);
              if (parsed.length > 0) {
                setHistory([[...parsed]]);
                setHistoryIdx(0);
                await offlineStorage.saveHighlights(userId, bookId, parsed);
                localStorage.removeItem(`pdf_notes_${bookId}`);
              }
            }
          }
        } catch {}
      } else {
        try {
          const savedNotes = localStorage.getItem(`pdf_notes_${bookId}`);
          if (savedNotes) {
            const parsed = JSON.parse(savedNotes);
            setHistory([[...parsed]]);
            setHistoryIdx(0);
          }
        } catch {}
      }

      // ── فحص الاتصال ──
      let online = false;
      if (navigator.onLine) {
        try {
          const ctrl = new AbortController();
          const tid  = setTimeout(() => ctrl.abort(), 3000);
          const res  = await fetch(
            'https://reescgwvnnbvwgtadvdv.supabase.co/rest/v1/',
            { method: 'HEAD', cache: 'no-store', signal: ctrl.signal }
          );
          clearTimeout(tid);
          online = res.status < 500;
        } catch { online = false; }
      }

      // ✅ سنستخدم Uint8Array بدلاً من blob URL
      let pdfUint8 = null;

      if (!online) {
        // ─────────────────────────────────────────
        //  وضع أوفلاين
        // ─────────────────────────────────────────
        const cached = await offlineStorage.loadSession();
        if (!cached) { setState('not-logged'); return; }
        const { userId: uid } = cached;
        if (!uid) { setState('not-logged'); return; }

        let rawData = await offlineStorage.loadPDF(uid, bookId);

        if (!rawData) {
          const allKeys = await getAllCachedPDFKeys();
          const matchKey = allKeys.find(k =>
            k === `pdf_${bookId}` || k === bookId ||
            k.includes(bookId) || bookId.includes(k.replace('pdf_', ''))
          );
          if (matchKey) {
            const cleanId = matchKey.replace(/^pdf_/, '');
            rawData = await offlineStorage.loadPDF(uid, cleanId);
          }
        }

        if (!rawData) {
          const db = await new Promise((resolve, reject) => {
            const req = indexedDB.open('khidma_offline_db');
            req.onsuccess = (e) => resolve(e.target.result);
            req.onerror   = () => reject();
          });
          const allPdfs = await new Promise((resolve) => {
            try {
              const tx  = db.transaction('pdfs', 'readonly');
              const req = tx.objectStore('pdfs').getAll();
              req.onsuccess = () => resolve(req.result || []);
              req.onerror   = () => resolve([]);
            } catch { resolve([]); }
          });
          const match = allPdfs.find(p =>
            p.book_id === bookId || p.id === `pdf_${bookId}` || p.id?.includes(bookId)
          );
          if (match) {
            const cleanId = match.book_id || match.id.replace(/^pdf_/, '');
            rawData = await offlineStorage.loadPDF(match.user_id || uid, cleanId);
          }
        }

        // ✅ تحويل الـ rawData لـ Uint8Array ثم إضافة watermark
        if (rawData) {
          let blob = null;
          if (rawData instanceof Blob) {
            blob = rawData;
          } else if (rawData instanceof ArrayBuffer || ArrayBuffer.isView(rawData)) {
            blob = new Blob([rawData], { type: 'application/pdf' });
          } else if (typeof rawData === 'string' && rawData.startsWith('blob:')) {
            const res = await fetch(rawData);
            blob = await res.blob();
          } else if (typeof rawData === 'string' && rawData.startsWith('data:')) {
            blob = await (await fetch(rawData)).blob();
          }

          if (blob) {
            pdfUint8 = await addWatermarkToPdf(blob, userInfo);
          }
        }

      } else {
        // ─────────────────────────────────────────
        //  وضع أونلاين
        // ─────────────────────────────────────────
        try {
          let targetUrl = null;

          if (directPdfUrl) {
  targetUrl = directPdfUrl;
} else if (bookId) {
  const { data: svc } = await supabase
    .from('services').select('file_url').eq('id', bookId).single();
  if (svc?.file_url) {
    targetUrl = getBackblazeDownloadUrl(svc.file_url);
  }
}

          if (targetUrl) {
            const response = await fetch(targetUrl);

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
              throw new Error('الرابط يعيد صفحة خطأ. تأكد من صحة الرابط.');
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const pdfBlob = await response.blob();
            if (pdfBlob.size < 100) throw new Error('الملف المحمّل فارغ');

            // ✅ إضافة الـ Watermark وإرجاع Uint8Array
            pdfUint8 = await addWatermarkToPdf(pdfBlob, userInfo);

            // ✅ حفظ النسخة الأصلية للـ offline (بدون watermark)
            const uid = currentUserIdRef.current;
            if (uid && bookId) {
              try {
                await offlineStorage.savePDF(uid, bookId, pdfBlob);
                console.log('[ReadBook] ✅ PDF saved offline successfully');
              } catch (e) {
                console.warn('[ReadBook] ⚠️ Failed to save offline:', e);
              }
            }
          } else {
            throw new Error('No file URL found');
          }

        } catch (err) {
          console.error('[ReadBook] Online fetch failed:', err);
          // محاولة التحميل من الكاش
          const cached = await offlineStorage.loadSession();
          if (cached) {
            const rawData = await offlineStorage.loadPDF(cached.userId, bookId);
            if (rawData) {
              let blob = rawData instanceof Blob
                ? rawData
                : new Blob([rawData], { type: 'application/pdf' });
              pdfUint8 = await addWatermarkToPdf(blob, userInfo);
            }
          }
        }
      }

      // ✅ تعيين المصدر كـ blob URL بعد الـ watermark
      if (pdfUint8) {
        const blob    = new Blob([pdfUint8], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        pdfBlobUrlRef.current = blobUrl;
        setPdfSrc(blobUrl);
        setState('ready');
      } else {
        setState('error');
      }
    }

    init();
  }, [bookId]);

  // ── حفظ التظليلات ──
  useEffect(() => {
    if (!bookId || !notes) return;
    try {
      localStorage.setItem(`pdf_notes_${bookId}`, JSON.stringify(notes));
    } catch {}
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      const userId = currentUserIdRef.current;
      if (!userId) return;
      try {
        await offlineStorage.saveHighlights(userId, bookId, notes);
      } catch {}
    }, 800);
  }, [notes, bookId]);

  // ── إخفاء أزرار الطباعة والتنزيل ──
  useEffect(() => {
    const remove = () => {
      [
        '[aria-label="Print"]','[title="Print"]',
        '[aria-label="طباعة"]','[title="طباعة"]',
        '[aria-label="Download"]','[title="Download"]',
        '[aria-label="تنزيل"]','[title="تنزيل"]',
      ].forEach(sel =>
        document.querySelectorAll(sel).forEach(el =>
          (el.closest('.rpv-toolbar__item') || el).style.display = 'none'
        )
      );
    };
    const obs = new MutationObserver(remove);
    obs.observe(document.body, { childList: true, subtree: true });
    remove();
    return () => obs.disconnect();
  }, []);

  // ── اختصارات لوحة المفاتيح ──
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey||e.metaKey) && ['p','P','s','S'].includes(e.key)) {
        e.preventDefault(); return;
      }
      if ((e.ctrlKey||e.metaKey) && !e.shiftKey && (e.key==='z'||e.key==='Z')) {
        e.preventDefault(); undo(); return;
      }
      if (
        ((e.ctrlKey||e.metaKey) && (e.key==='y'||e.key==='Y')) ||
        ((e.ctrlKey||e.metaKey) && e.shiftKey && (e.key==='z'||e.key==='Z'))
      ) {
        e.preventDefault(); redo(); return;
      }
    };
    const noCtx = (e) => e.preventDefault();
    document.addEventListener('keydown',     handler);
    document.addEventListener('contextmenu', noCtx);
    return () => {
      document.removeEventListener('keydown',     handler);
      document.removeEventListener('contextmenu', noCtx);
    };
  }, [undo, redo]);

  // ─────────────────────────────────────────────
  //  Highlight Plugin Renders
  // ─────────────────────────────────────────────
  const renderHighlightTarget = (props) => (
    <div style={{
      position:'absolute',
      left:`${props.selectionRegion.left}%`,
      top:`${props.selectionRegion.top + props.selectionRegion.height}%`,
      transform:'translateY(6px)', zIndex:9999,
    }}>
      <div className="highlight-popup">
        <span className="highlight-popup-label">تظليل:</span>
        {COLORS.map(({ hex, label }) => (
          <div key={hex} className="popup-color-btn"
            style={{ backgroundColor: hex }} title={label}
            onClick={() => {
              currentColorRef.current = hex;
              setCurrentColor(hex);
              props.toggle();
            }}
          />
        ))}
        <div className="divider" />
        <button
          className="popup-copy-btn"
          onClick={() => {
            try {
              navigator.clipboard.writeText(props.selectedText || '');
              showToast('📋 تم النسخ');
            } catch {
              const el = document.createElement('textarea');
              el.value = props.selectedText || '';
              document.body.appendChild(el);
              el.select();
              document.execCommand('copy');
              document.body.removeChild(el);
              showToast('📋 تم النسخ');
            }
            props.cancel();
          }}
        >
          📋 نسخ
        </button>
      </div>
    </div>
  );

  const renderHighlightContent = (props) => {
    const newNote = {
      id: Date.now(), content: '',
      highlightAreas: props.highlightAreas,
      quote: props.selectedText,
      color: currentColorRef.current,
    };
    pushNotes([...notes, newNote]);
    props.cancel();
    return <></>;
  };

  const renderHighlights = (props) => (
    <div>
      {(notes || [])
        .filter(n => n.highlightAreas?.some(a => a.pageIndex === props.pageIndex))
        .map(note =>
          note.highlightAreas
            .filter(a => a.pageIndex === props.pageIndex)
            .map((area, idx) => (
              <div key={`${note.id}-${idx}`} style={{
                ...props.getCssProperties(area, props.rotation),
                background: note.color,
                mixBlendMode: 'multiply',
                opacity: 0.55,
                position: 'absolute',
                borderRadius: '2px',
                cursor: 'pointer',
              }} />
            ))
        )}
    </div>
  );

  const highlightPluginInstance = highlightPlugin({
    renderHighlightTarget,
    renderHighlightContent,
    renderHighlights,
    trigger: Trigger.TextSelection,
  });

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: {
      renderDefaultToolbar: (Toolbar) => (
        <Toolbar>
          {(slots) => {
            const {
              CurrentPageInput, GoToNextPage, GoToPreviousPage,
              NumberOfPages, ShowSearchPopover, Zoom, ZoomIn, ZoomOut, EnterFullScreen,
            } = slots;
            return (
              <div style={{ display:'flex', alignItems:'center', width:'100%', gap:'4px', padding:'0 8px', direction:'ltr' }}>
                <ShowSearchPopover />
                <div style={{ flex:1 }} />
                <GoToPreviousPage />
                <div style={{ display:'flex', alignItems:'center', gap:'4px', color:'#ccc', fontSize:'13px', direction:'ltr' }}>
                  <CurrentPageInput /> / <NumberOfPages />
                </div>
                <GoToNextPage />
                <div style={{ flex:1 }} />
                <ZoomOut /><Zoom /><ZoomIn />
                <EnterFullScreen />
              </div>
            );
          }}
        </Toolbar>
      ),
    },
  });

  // ─────────────────────────────────────────────
  //  Render States
  // ─────────────────────────────────────────────
  if (state === 'loading') return (
    <div className="loading-screen">
      <style>{css}</style>
      <div className="loading-spinner" />
      <div className="loading-text">جاري تحميل الكتاب...</div>
    </div>
  );
  if (state === 'not-logged') return (
    <div className="loading-screen">
      <style>{css}</style>
      <div style={{ fontSize:'40px' }}>🔒</div>
      <div className="loading-text">يجب تسجيل الدخول أولاً</div>
    </div>
  );
  if (state === 'error') return (
    <div className="loading-screen">
      <style>{css}</style>
      <div style={{ fontSize:'40px' }}>⚠️</div>
      <div className="loading-text">تعذّر تحميل الكتاب — تأكد من تخزينه للاستخدام بدون إنترنت</div>
    </div>
  );

  // ─────────────────────────────────────────────
  //  Main Render
  // ─────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>

      {showSplash && (
        <div className="splash-screen" onAnimationEnd={() => setShowSplash(false)}>
          <div className="splash-logo-ring">
            <img src={koalaImg} alt="Koala Reader" />
          </div>
          <div className="splash-name">Koala Reader</div>
          <div className="splash-sub">قارئ الكتب</div>
          <div className="splash-bar-wrap">
            <div className="splash-bar" />
          </div>
        </div>
      )}

      <div className="custom-header">
        <div className="header-title">
          <span>🐨</span>
          <span>Koala Reader</span>
        </div>

        {/* ✅ شارة الـ Watermark */}
        {userEmail && (
          <div className="watermark-badge">
            🔒 {userEmail.length > 20 ? userEmail.slice(0, 18) + '…' : userEmail}
          </div>
        )}

        <button className="download-btn" onClick={handleDownload} title="تحميل الكتاب">
          ⬇️ تحميل
        </button>

        <div className="undo-redo-group">
          <button className="undo-redo-btn" onClick={undo} disabled={!canUndo} title="تراجع (Ctrl+Z)">
            ↩{canUndo && <span className="badge">{historyIdx}</span>}
          </button>
          <button className="undo-redo-btn" onClick={redo} disabled={!canRedo} title="إعادة (Ctrl+Y)">
            ↪{canRedo && <span className="badge">{history.length - 1 - historyIdx}</span>}
          </button>
        </div>

        <div className="divider" />

        <div className="header-section">
          <span className="section-label">لون التظليل:</span>
          {COLORS.map(({ hex, label }) => (
            <div key={hex}
              className={`color-picker ${currentColor === hex ? 'active' : ''}`}
              style={{ backgroundColor: hex }} title={label}
              onClick={() => setCurrentColor(hex)}
            />
          ))}
        </div>
      </div>

      <div style={{ height: 'calc(100vh - 56px)' }}>
        {/* ✅ pdfSrc الآن هو { data: Uint8Array } - لا blob URL */}
        {pdfSrc && workerUrl && (
          <Worker workerUrl={workerUrl}>
            <Viewer
              fileUrl={pdfSrc}
              plugins={[defaultLayoutPluginInstance, highlightPluginInstance]}
            />
          </Worker>
        )}
      </div>

      {toastMsg && <div className="toast">{toastMsg}</div>}
    </>
  );
}